import { Fragment, useState } from 'react';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";

import { postDataWithHeaderHelper } from "../requests/PostRequests";

import LoadingIcon from "./LoadingIcon";
import { InputGroup, Table } from 'react-bootstrap';
import { DeleteConfirmation } from "./DeleteConfirmation";

const defaultLocation = {
  id: null,
  name: "",
  address: "",
  postal: "",
};

function LocationForm({
  currentLocation,
  showFlag,
  onFormClose,
  onCreate,
  onUpdate }) {

  const [locationForm, setLocationForm] = useState(currentLocation);
  const onSave = currentLocation.id == null ? onCreate : onUpdate;
  const [validated, setValidated] = useState(false);

  function onFormChange(e) {
    setLocationForm({ ...locationForm, [e.target.name]: e.target.value });
  }

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();

    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      onSave(locationForm);
    }

    setValidated(true);
  };

  return (
    <Modal size="lg" centered backdrop="static" keyboard={false} show={showFlag} onHide={onFormClose}>
      <Modal.Header closeButton>
        <Modal.Title>{locationForm.id == null ? "New Location" : `Location ID: ${locationForm.id}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Set the name */}
          <Form.Group
            className="mb-3"
            controlId="locationForm.name"
          >
            <Form.Label>Name</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                required
                name="name"
                defaultValue={locationForm.name}
                placeholder="Location Name"
                onChange={onFormChange} />
              <Form.Control.Feedback type="invalid">
                Please enter a name.
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          {/* Set the address */}
          <Form.Group
            className="mb-3"
            controlId="locationForm.address"
          >
            <Form.Label>Address</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                required
                name="address"
                defaultValue={locationForm.address}
                placeholder="Location Address"
                onChange={onFormChange} />
              <Form.Control.Feedback type="invalid">
                Please enter an address.
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          {/* Set the postal */}
          <Form.Group
            className="mb-3"
            controlId="locationForm.postal"
          >
            <Form.Label>Postal Code</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                required
                name="postal"
                defaultValue={locationForm.postal}
                placeholder="Postal Code"
                onChange={onFormChange} />
              <Form.Control.Feedback type="invalid">
                Please choose a postal code.
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Button type="submit" variant="primary">
            Save Changes
          </Button>
        </Form>
      </Modal.Body >
      {/* User should be able to close or save current location changes */}
      <Modal.Footer>
        <Button variant="secondary" onClick={onFormClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal >
  );
}

const Location = ({ location, index, onEdit, onDelete }) => {
  return (
    <Fragment key={`${location.id}_location_fragment_container`}>
      <tr>
        <td>
          <Card.Subtitle>
            {location.name}
          </Card.Subtitle>
          <Card.Subtitle>
            {location.address}
          </Card.Subtitle>
          <Card.Subtitle>
            {location.postal}
          </Card.Subtitle>
        </td>
        <td>
          <Button key={`${index}_edit_location_btn`} variant="primary" onClick={() => onEdit(location.id)}>Edit</Button>
        </td>
        <td>
          <Button key={`${index}_delete_location_btn`} variant="danger" onClick={() => onDelete(location.id)}>Delete</Button>
        </td>
      </tr>
    </Fragment>
  );
}

const Locations = ({ locations, onEdit, onDelete }) => {
  return (
    <Table responsive="sm">
      <tbody>
        <tr>
          <th>Location</th>
          <th>Edit</th>
          <th>Delete</th>
        </tr>
        {
          locations.length > 0
            ? locations.map(function (location, i) {
              return <Location
                key={`${location.id}_location_component`}
                location={location}
                index={i}
                onEdit={onEdit}
                onDelete={onDelete} />
            })
            :
            <tr>
              <td style={{ padding: "50px 0px 25px 0px" }} colSpan={3}>
                Sorry, No locations found. Please add a new location by clicking the "Add New Location" button below.
              </td>
            </tr>
        }
      </tbody>
    </Table>
  )
}

const LocationsModal = ({
  onTriggerMessage,
  locations,
  showFlag,
  isLoading,
  onFormClose,
  onLocationAdd,
  onLocationEdit,
  onLocationDelete
}) => {
  const [currentLocation, setCurrentLocation] = useState(defaultLocation);

  const [showLocationFormFlag, setShowLocationFormFlag] = useState(false);
  const showLocationForm = () => setShowLocationFormFlag(true);
  const closeLocationForm = () => setShowLocationFormFlag(false);

  const [showDeleteConfirmationFlag, setShowDeleteConfirmationFlag] = useState(false);
  const showDeleteConfirmation = () => setShowDeleteConfirmationFlag(true);
  const closeDeleteConfirmation = () => setShowDeleteConfirmationFlag(false);

  /*******************************************************************************************************
   *  LocationForm API functions
   ******************************************************************************************************/
  async function postLocation(location) {
    const onSuccess = (data) => {
      const rows = data.result.rows;
      const newLocationId = rows[0].id;

      let updatedLocation = { ...location };
      updatedLocation.id = newLocationId;

      onLocationAdd(updatedLocation);

      onTriggerMessage(
        "Success",
        "Successfully uploaded location to the server",
      );

      closeLocationForm();
    };

    // Upload location details
    const submitObject = {
      name: location.name,
      address: location.address,
      postal: location.postal,
    };

    await postDataWithHeaderHelper("/v1/locations/add", submitObject, onTriggerMessage, onSuccess);
  }

  async function postUpdateLocation(location) {
    const onSuccess = (_) => {
      onLocationEdit(location);
      onTriggerMessage(
        "Success",
        "Successfully updated location to the server",
      );
    };

    // Update location details
    const submitObject = {
      id: location.id,
      name: location.name,
      address: location.address,
      postal: location.postal,
    };

    await postDataWithHeaderHelper("/v1/locations/update", submitObject, onTriggerMessage, onSuccess);
  }

  async function postDeleteLocation(location) {
    const onSuccess = (_) => {

      onTriggerMessage(
        "Success",
        "Successfully deleted location from the server",
      );

      closeDeleteConfirmation();
      onLocationDelete(location);
      setCurrentLocation({ ...defaultLocation });
    };

    // Delete location
    const submitObject = {
      id: location.id,
    };

    await postDataWithHeaderHelper("/v1/locations/delete",
      submitObject,
      onTriggerMessage,
      onSuccess);
  }

  /*******************************************************************************************************
   *  Locations with EditCourse functions
   ******************************************************************************************************/
  /**
   * We should submit the updated location to API and update EditCourses
   */
  async function onEdit(locationForm) {
    await postUpdateLocation(locationForm);
  }

  /**
   * We should submit the new location to API and add to EditCourses
   */
  async function onAdd(locationForm) {
    await postLocation(locationForm);
    setCurrentLocation({ ...defaultLocation });
  }

  /**
   * We should delete the location using API and remove from EditCourses
   */
  async function onDeleteLocationConfirmation() {
    await postDeleteLocation({ ...currentLocation });
  }

  /*******************************************************************************************************
   *  LocationForm functions
   ******************************************************************************************************/
  function onDelete(id) {
    const index = locations.findIndex((l) => l.id == id);
    setCurrentLocation(locations[index]);
    showDeleteConfirmation();
  }

  function onAddLocation() {
    setCurrentLocation({ ...defaultLocation });
    showLocationForm();
  }

  function onEditLocation(id) {
    const index = locations.findIndex((l) => l.id == id);
    setCurrentLocation(locations[index]);
    showLocationForm();
  }

  return (
    <>
      <Modal size="lg" centered backdrop="static" keyboard={false} show={showFlag} onHide={onFormClose}>
        <Modal.Header closeButton>
          <Modal.Title>Locations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading
            ? <LoadingIcon />
            : <Locations
              locations={locations}
              onEdit={onEditLocation}
              onDelete={onDelete} />
          }
        </Modal.Body >
        {/* User should be able to close the locations modal */}
        <Modal.Footer>
          <Button onClick={onAddLocation}>Add New Location</Button>
          <Button variant="secondary" onClick={onFormClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal >
      {/* Display the delete confirmation below we allow deletion */}
      <DeleteConfirmation
        title={`${currentLocation.name} at ${currentLocation.address}`}
        description="Are you sure you want to delete this location?"
        showFlag={showDeleteConfirmationFlag}
        onConfirmation={onDeleteLocationConfirmation}
        onClose={closeDeleteConfirmation} />
      {/* Show the location form to update the location */}
      {showLocationFormFlag
        ? <LocationForm
          currentLocation={currentLocation}
          showFlag={showLocationFormFlag}
          onFormClose={closeLocationForm}
          onCreate={onAdd}
          onUpdate={onEdit} />
        : <></>}
    </>
  );
};

export default LocationsModal;
